import { type ReactElement, useMemo } from 'react';
import { format, isValid, parseISO, subMonths } from 'date-fns';
import type { Styling } from 'common/types/dashboard/dashboardDefinition';
import { DASHBOARD_METRIC_CATALOG } from 'common/types/dashboard/dashboardVocabularies';
import {
  type ChartConfig,
  type ChartSeries,
  ScalarBarChart,
  ScalarLineChart,
  ScalarPieChart,
} from 'components/Charts';
import { Box, Skeleton, Text } from 'components/ui';
import { CHART_PALETTE, getChartColor } from 'components/WithChart/chartColors';
import type { ResolvedChartSeries } from '../../../dashboardData.types';
import { SUMMARY_DASHBOARD_TEST_IDS } from '../../../SummaryDashboard.constants';
import { COMPONENT_TYPE_LABELS } from '../dashboardComponent.helpers';
import DashboardComponentEditButton from '../DashboardComponentEditButton';
import {
  tileBadgeSx,
  tileCardSx,
  tileHeaderActionsSx,
  tileHeaderSx,
  tileSubtitleSx,
  tileTitleSx,
} from '../DashboardGrid.styles';
import { formatMetricValue, resolveNumberFormat } from '../dashboardValue.helpers';
import type { ChartCardProps } from './ChartCard.props';
import { chartBodySx, chartCaptionSx, chartHeightPx, chartMessageSx } from './ChartCard.styles';

const ERROR_MESSAGE = 'Data unavailable';
const NO_DATA_MESSAGE = 'No data for this selection';
const NEGATIVE_PIE_MESSAGE = 'Pie charts cannot represent negative values. Choose a bar chart for this metric.';
const EMPTY_VALUE = '—';
const MAX_CATEGORY_LABEL_LENGTH = 16;
const PIE_INNER_RADIUS = 0.5;
const LEGEND_HEIGHT_PX = 28;
const UNIT_LABELS: Record<string, string> = {
  count: 'count',
  money: 'currency',
  multiple: 'multiple',
  percent: 'percentage',
};

const TIME_RANGE_MONTHS: Partial<Record<NonNullable<Styling['time_range']>['default'] & string, number>> = {
  '1Y': 12,
  '3M': 3,
  '6M': 6,
};

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const isIsoDate = (value: string): boolean => ISO_DATE_PATTERN.test(value) && isValid(parseISO(value));

const formatCategory = (value: string | number): string => {
  const text = String(value);
  if (isIsoDate(text)) {
    return format(parseISO(text), 'MMM yyyy');
  }
  return text.length > MAX_CATEGORY_LABEL_LENGTH
    ? `${text.slice(0, MAX_CATEGORY_LABEL_LENGTH - 1)}…`
    : text;
};

interface ChartView {
  categories: string[];
  series: ResolvedChartSeries[];
}

const metricUnit = (metric: string): string =>
  (DASHBOARD_METRIC_CATALOG as Record<string, { kind?: string } | undefined>)[metric]?.kind ?? 'numeric';

// `styling.time_range.default` windows a date axis back from its newest date. It is the stored
// default only: there is no on-tile selector yet, so the window is whatever the config says. A window
// holding fewer than two dates (one recent valuation, nothing else that year) is not a trend, so the
// full history is shown instead of a lone dot.
const applyTimeRange = (view: ChartView, range: Styling['time_range']): ChartView => {
  const months = range?.default ? TIME_RANGE_MONTHS[range.default] : undefined;
  const { categories } = view;
  if (!months || categories.length === 0 || !categories.every(isIsoDate)) {
    return view;
  }

  const cutoff = subMonths(parseISO(categories[categories.length - 1]), months);
  const kept = categories.flatMap((category, index) => (parseISO(category) >= cutoff ? [index] : []));
  if (kept.length < 2) {
    return view;
  }

  return {
    categories: kept.map(index => categories[index]),
    series: view.series.map(item => ({ ...item, values: kept.map(index => item.values[index]) })),
  };
};

// Each metric keeps its own hashed colour where it can, so it matches the spreadsheet chart popovers.
// Two metrics can hash to the same slot (total_value and invested_capital do), which would draw two
// identical series, so a clash takes the next free palette colour. Explicit `styling.colors` win.
const assignSeriesColors = (metrics: string[], overrides: readonly string[] = []): string[] => {
  const used = new Set<string>();
  return metrics.map((metric, index) => {
    const override = overrides[index];
    const hashed = getChartColor(metric);
    const color = override ?? (used.has(hashed) ? (CHART_PALETTE.find(entry => !used.has(entry)) ?? hashed) : hashed);
    used.add(color);
    return color;
  });
};

/**
 * Bar, line or pie tile for a dashboard chart component.
 *
 * The backend resolves `categories` and one aligned `series` per metric, and applies the same
 * compatibility rules the edit modal shows. A metric it cannot plot comes back with a reason and
 * no values, and is listed under the chart instead of being drawn as a flat zero.
 */
const ChartCard = ({
  component,
  data,
  currencyCode = 'USD',
  isLoading = false,
  isError = false,
  onEdit,
}: ChartCardProps): ReactElement => {
  const { display = {}, styling = {}, position, type } = component;
  const heightPx = chartHeightPx(position);

  const view = useMemo(
    () => applyTimeRange({ categories: data?.categories ?? [], series: data?.series ?? [] }, styling.time_range),
    [data, styling.time_range]
  );

  const unitCompatibleSeries = useMemo(() => {
    const lead =
      view.series.find(item => !item.unsupportedReason && item.values.some(value => value !== null)) ??
      view.series.find(item => !item.unsupportedReason);
    if (!lead) {
      return view.series;
    }

    const leadUnit = metricUnit(String(lead.metric));
    return view.series.map(item => {
      if (item.unsupportedReason) {
        return item;
      }
      const unit = metricUnit(String(item.metric));
      return unit === leadUnit
        ? item
        : {
          ...item,
          unsupportedReason: `It uses ${UNIT_LABELS[unit] ?? unit} units and cannot share an axis with ${
            UNIT_LABELS[leadUnit] ?? leadUnit
          } values.`,
        };
    });
  }, [view.series]);

  const plottable = unitCompatibleSeries.filter(item => !item.unsupportedReason);
  const skipped = unitCompatibleSeries.filter(item => item.unsupportedReason);
  const isSingleSeries = plottable.length === 1;

  const formatterFor = (metric: string) => {
    const numberFormat = resolveNumberFormat({ isSingleMetric: isSingleSeries, metric, styling });
    return (value: unknown): string =>
      typeof value === 'number' ? formatMetricValue({ currencyCode, format: numberFormat, metric, value }) : EMPTY_VALUE;
  };

  // The value axis follows the first plotted metric, compacted: "$120M" fits an axis, a full
  // currency figure does not.
  const leadMetric = plottable[0]?.metric ? String(plottable[0].metric) : '';
  const leadFormat = leadMetric ? resolveNumberFormat({ isSingleMetric: isSingleSeries, metric: leadMetric, styling }) : 'decimal';
  const axisFormatter = (value: string | number): string =>
    typeof value === 'number'
      ? formatMetricValue({
        currencyCode,
        format: leadFormat === 'currency' ? 'currency_compact' : leadFormat,
        metric: leadMetric,
        value,
      })
      : String(value);

  const isHorizontal = type === 'bar_chart' && styling.orientation === 'horizontal';
  // A horizontal axis draws its first category at the bottom; reversing keeps rank 1 on top.
  const orderedCategories = isHorizontal ? [...view.categories].reverse() : view.categories;
  const indexOf = (category: string) => view.categories.indexOf(category);

  const seriesColors = assignSeriesColors(
    plottable.map(item => String(item.metric)),
    styling.colors
  );
  const chartSeries: ChartSeries[] = plottable.map((item, index) => {
    const metric = String(item.metric);
    return {
      color: seriesColors[index],
      // ECharts reads a series-level tooltip formatter, so values keep their metric's unit.
      config: { tooltip: { valueFormatter: formatterFor(metric) } },
      data: orderedCategories.flatMap(category => {
        const value = item.values[indexOf(category)];
        // A missing value is left out rather than plotted: "no data" is not zero.
        return value === null || value === undefined ? [] : [{ x: category, y: value }];
      }),
      id: metric,
      label: item.label,
    };
  });

  const hasNegativePieValue =
    type === 'pie_chart' && chartSeries.some(item => item.data.some(point => point.y < 0));
  const hasPoints =
    type === 'pie_chart'
      ? chartSeries.some(item => item.data.some(point => point.y > 0))
      : chartSeries.some(item => item.data.length > 0);
  const showLegend = styling.show_legend ?? (type === 'pie_chart' || chartSeries.length > 1);
  const isTimeAxis = type === 'line_chart' && view.categories.length > 0 && view.categories.every(isIsoDate);

  const categoryAxis: ChartConfig['xAxis'] = isTimeAxis
    // A time axis labels its own ticks; a category formatter would receive timestamps.
    ? { type: 'time' }
    : { formatter: formatCategory };
  const config: ChartConfig = {
    legend: { position: 'bottom', show: showLegend },
    // `options` replaces these keys wholesale. The adapter's defaults reserve a title band and lay
    // the legend out over several rows, which a tile this small cannot afford: a pie's legend lands
    // on the donut. One scrolling legend row at the bottom, and a grid that uses the rest.
    options: {
      grid: { bottom: showLegend ? LEGEND_HEIGHT_PX : 4, containLabel: true, left: 4, right: 12, top: 12 },
      legend: { bottom: 0, left: 'center', show: showLegend, type: 'scroll' },
      ...(styling.colors?.length && { color: styling.colors }),
    },
    xAxis: isHorizontal ? { formatter: axisFormatter } : categoryAxis,
    yAxis: isHorizontal ? { formatter: formatCategory } : { formatter: axisFormatter },
  };

  const style = { height: `${heightPx}px`, width: '100%' };

  const renderChart = (): ReactElement => {
    if (type === 'pie_chart') {
      const [first] = chartSeries;
      const slices = (first?.data ?? []).filter(point => point.y > 0);
      const pieFormatter = first ? formatterFor(first.id) : () => EMPTY_VALUE;
      return (
        <ScalarPieChart
          config={{
            ...config,
            tooltip: { formatter: point => `${point.x}: ${pieFormatter(point.y)}` },
          }}
          data={slices}
          innerRadius={PIE_INNER_RADIUS}
          label={first?.label}
          notMerge
          showLabels={false}
          style={style}
        />
      );
    }
    if (type === 'line_chart') {
      return (
        <ScalarLineChart
          area={styling.fill === 'area'}
          config={config}
          notMerge
          series={chartSeries}
          style={style}
        />
      );
    }
    return (
      <ScalarBarChart
        // The backend ranks categories (largest first, "Other" last). Pass the full list so a
        // category stays in place even when every plotted series has a missing value there.
        categoryOrder={orderedCategories}
        config={config}
        horizontal={isHorizontal}
        notMerge
        series={chartSeries}
        style={style}
      />
    );
  };

  const renderBody = (): ReactElement => {
    if (isLoading) {
      return (
        <Box data-testid={SUMMARY_DASHBOARD_TEST_IDS.chartCardLoading}>
          <Skeleton height={heightPx} variant="rect" width="100%" />
        </Box>
      );
    }
    if (isError) {
      return (
        <Box data-testid={SUMMARY_DASHBOARD_TEST_IDS.chartCardError} sx={chartMessageSx(heightPx)}>
          {ERROR_MESSAGE}
        </Box>
      );
    }
    if (data?.status === 'unsupported') {
      return (
        <Box data-testid={SUMMARY_DASHBOARD_TEST_IDS.chartCardUnsupported} sx={chartMessageSx(heightPx)}>
          {data.unsupportedReason ?? NO_DATA_MESSAGE}
        </Box>
      );
    }
    if (hasNegativePieValue) {
      return (
        <Box data-testid={SUMMARY_DASHBOARD_TEST_IDS.chartCardUnsupported} sx={chartMessageSx(heightPx)}>
          {NEGATIVE_PIE_MESSAGE}
        </Box>
      );
    }
    if (!hasPoints) {
      // Every metric unplottable reads better as its reason than as a bare "no data".
      const message = plottable.length === 0 && skipped[0]?.unsupportedReason ? skipped[0].unsupportedReason : NO_DATA_MESSAGE;
      return (
        <Box data-testid={SUMMARY_DASHBOARD_TEST_IDS.chartCardEmpty} sx={chartMessageSx(heightPx)}>
          {message}
        </Box>
      );
    }
    return (
      <>
        {renderChart()}
        {skipped.map(item => (
          <Text
            component="p"
            data-testid={SUMMARY_DASHBOARD_TEST_IDS.chartCardSkippedMetric}
            key={String(item.metric)}
            sx={chartCaptionSx}>
            {`${item.label} not shown: ${item.unsupportedReason}`}
          </Text>
        ))}
      </>
    );
  };

  return (
    <Box data-component-type={type} data-testid={SUMMARY_DASHBOARD_TEST_IDS.componentCard} id={component.id} sx={tileCardSx}>
      <Box sx={tileHeaderSx}>
        <Box sx={{ minWidth: 0 }}>
          {Boolean(display.title) && (
            <Text component="p" data-testid={SUMMARY_DASHBOARD_TEST_IDS.componentCardTitle} sx={tileTitleSx}>
              {display.title}
            </Text>
          )}
          {Boolean(display.subtitle) && (
            <Text component="p" sx={tileSubtitleSx}>
              {display.subtitle}
            </Text>
          )}
        </Box>
        {onEdit && (
          <Box sx={tileHeaderActionsSx}>
            <DashboardComponentEditButton
              label={display.title?.trim() || COMPONENT_TYPE_LABELS[type]}
              onClick={onEdit}
            />
          </Box>
        )}
      </Box>

      {Boolean(display.badge?.text) && (
        <Box component="span" sx={tileBadgeSx}>
          {display.badge?.text}
        </Box>
      )}

      <Box data-testid={SUMMARY_DASHBOARD_TEST_IDS.chartCard} sx={chartBodySx}>
        {renderBody()}
      </Box>
    </Box>
  );
};

export default ChartCard;
